import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PillChip } from "@/components/toggles";
import { X, Sparkles, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * BuyerTryOnModal — V2-Konfigurator laut Modul-G-Spec §5.
 * Owner-Vorgabe E3: Buyer wählt Pose + Tragedauer (in Seller-Range) +
 * Aktivitäts-Presets (Seller-freigegeben). System berechnet State-Vektor.
 */
export default function BuyerTryOnModal({ listingId, onClose }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("LOAD");      // LOAD | CONFIG | REQUESTING | POLLING | RESULT | ERROR | UNAVAILABLE
  const [cfg, setCfg] = useState(null);
  const [poseIndex, setPoseIndex] = useState(null);
  const [days, setDays] = useState(1);
  const [activities, setActivities] = useState([]);
  const [request, setRequest] = useState(null);
  const [artifact, setArtifact] = useState(null);
  const [error, setError] = useState("");
  const [poseZoom, setPoseZoom] = useState(null);   // pose_index für Vollbild-Overlay

  useEffect(() => {
    api.get(`/listings/${listingId}/preview-config`).then((res) => {
      const d = res.data || {};
      if (!d.available) { setPhase("UNAVAILABLE"); setError(d.reason || "TRY_ON_DISABLED"); return; }
      setCfg(d);
      setDays(d.wear_duration_days?.min || 1);
      const readyPoses = (d.subject?.poses || []).filter((p) => p.baseline_ready);
      const primary = readyPoses.find((p) => p.is_primary) || readyPoses[0];
      if (primary) setPoseIndex(primary.pose_index);
      setPhase("CONFIG");
    }).catch((e) => {
      setError(e.response?.data?.detail?.message || e.response?.data?.detail || "Fehler beim Laden");
      setPhase("ERROR");
    });
  }, [listingId]);

  // Baseline-Auto-Poll solange nicht alle bereit sind
  useEffect(() => {
    if (phase !== "CONFIG" || !cfg) return;
    const allReady = (cfg.subject?.poses || []).every((p) => p.baseline_ready);
    if (allReady) return;
    const id = setInterval(async () => {
      try {
        const res = await api.get(`/listings/${listingId}/preview-config`);
        if (res.data?.available) {
          setCfg(res.data);
          if (poseIndex === null) {
            const ready = (res.data.subject?.poses || []).find((p) => p.baseline_ready);
            if (ready) setPoseIndex(ready.pose_index);
          }
        }
      } catch { /* noop */ }
    }, 5000);
    return () => clearInterval(id);
  }, [phase, cfg, listingId]);

  const toggleActivity = (code) => {
    setActivities((cur) => cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]);
  };

  const submit = async () => {
    setPhase("REQUESTING"); setError("");
    try {
      const res = await api.post(`/listings/${listingId}/try-on`, {
        wear_duration_days: parseInt(days, 10),
        activity_presets: activities,
        pose_index: poseIndex,
      });
      setRequest(res.data);
      setPhase("POLLING");
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.response?.data?.detail || "Fehler beim Anfordern");
      setPhase("ERROR");
    }
  };

  useEffect(() => {
    if (phase !== "POLLING" || !request?.request_id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/listings/${listingId}/try-on/${request.request_id}`);
        if (cancelled) return;
        const st = res.data.request?.status;
        if (st === "COMPLETED" && res.data.artifact) {
          setArtifact(res.data.artifact);
          setRequest(res.data.request);
          setPhase("RESULT");
        } else if (st === "FAILED") {
          setError(res.data.request?.failure_reason || "Rendering fehlgeschlagen");
          setPhase("ERROR");
        }
      } catch { /* noop */ }
    };
    const id = setInterval(poll, 4000);
    poll();
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, request, listingId]);

  return (
    <div data-testid="buyer-try-on-modal" className="fixed inset-0 z-[9999] bg-black/80 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        <div className="sticky top-0 bg-white flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5] z-10">
          <p className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-1.5"><Sparkles size={14} /> Virtuell anprobieren</p>
          <button onClick={onClose} className="p-1.5 -mr-1.5 rounded-full hover:bg-[#F5F5F5]" data-testid="buyer-try-on-close">
            <X size={18} className="text-[#0A0A0A]" />
          </button>
        </div>

        {phase === "LOAD" && (
          <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-[#666]" /></div>
        )}

        {phase === "UNAVAILABLE" && (
          <div className="p-6 text-center" data-testid="buyer-try-on-unavailable">
            <AlertCircle size={28} className="mx-auto text-[#CCC] mb-2" />
            <p className="text-sm">{t("buyer_try_on.unavailable")}</p>
            <p className="text-[10px] text-[#999] mt-1">{error === "SELLER_NOT_READY" ? "Seller hat noch kein Motiv" : "Nicht aktiviert"}</p>
          </div>
        )}

        {phase === "CONFIG" && cfg && (
          <div className="p-4 space-y-4" data-testid="buyer-try-on-config">
            {cfg.consent_note && (
              <div className="text-[11px] border-l-2 border-[#0A0A0A] pl-2 text-[#333]">{cfg.consent_note}</div>
            )}
            {/* Posen (Baselines only — Anonymitätsschutz) */}
            {cfg.subject?.poses?.length > 0 && (
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-wider text-[#999]">{t("buyer_try_on.choose_base_pose")}</p>
                </div>
                <p className="text-[10px] text-[#666] mt-0.5 leading-snug">
                  Das ist die Referenz-Pose. Der Artikel wird erst appliziert, wenn du unten auf &bdquo;Vorschau erstellen&ldquo; klickst.
                </p>
                <div className={`grid gap-2 mt-1 ${cfg.subject.poses.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {cfg.subject.poses.map((p) => {
                    const ready = p.baseline_ready && p.baseline_url;
                    const isSelected = poseIndex === p.pose_index;
                    return (
                      <div key={p.pose_index} className="space-y-1">
                        <button
                          data-testid={`buyer-try-on-pose-${p.pose_index}`}
                          onClick={() => ready && setPoseZoom(p.pose_index)}
                          disabled={!ready}
                          className={`relative w-full border aspect-[3/4] overflow-hidden bg-[#F5F5F5] ${isSelected ? "border-[#0A0A0A] ring-2 ring-[#0A0A0A]" : "border-[#E5E5E5]"} ${!ready ? "opacity-60 cursor-wait" : "cursor-zoom-in"}`}>
                          {ready ? (
                            <img src={p.baseline_url} alt={`Pose ${p.pose_index}`} className="w-full h-full object-contain" />
                          ) : (
                            <div className="absolute inset-0 bg-[#F5F5F5] flex flex-col items-center justify-center gap-1">
                              <Loader2 size={18} className="animate-spin text-[#999]" />
                              <span className="text-[10px] text-[#999] text-center px-2">KI erstellt Vorschau …</span>
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute top-1.5 left-1.5 bg-[#0A0A0A] text-white text-[9px] font-semibold px-1.5 py-0.5 rounded">Aktiv</div>
                          )}
                        </button>
                        <PillChip
                          testId={`buyer-try-on-pose-select-${p.pose_index}`}
                          onClick={() => ready && setPoseIndex(p.pose_index)}
                          disabled={!ready}
                          active={isSelected}
                          variant="neutral"
                          size="sm"
                          className="w-full !justify-center"
                        >
                          {isSelected ? "Gew\u00E4hlt" : "Diese Pose"}
                        </PillChip>
                      </div>
                    );
                  })}
                </div>
                {cfg.subject.poses.some((p) => !p.baseline_ready) && (
                  <p className="text-[10px] text-[#999] mt-1" data-testid="buyer-try-on-baseline-notice">
                    Einige Posen werden noch vorbereitet. Bitte kurz warten oder eine bereits fertige Pose w&auml;hlen.
                  </p>
                )}
              </div>
            )}

            {/* Tragedauer */}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#999]">Tragedauer: <span className="text-[#0A0A0A] font-mono">{days} {days === 1 ? "Tag" : "Tage"}</span></p>
              <input
                data-testid="buyer-try-on-days"
                type="range"
                min={cfg.wear_duration_days.min}
                max={cfg.wear_duration_days.max}
                step="1"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="w-full mt-2"
              />
              <div className="flex justify-between text-[9px] text-[#999]">
                <span>{cfg.wear_duration_days.min}</span>
                <span>{cfg.wear_duration_days.max}</span>
              </div>
            </div>

            {/* Aktivitäten */}
            {cfg.activity_presets?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#999]">{t("buyer_try_on.activity")}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {cfg.activity_presets.map((a) => {
                    const on = activities.includes(a.code);
                    return (
                      <PillChip
                        key={a.code}
                        testId={`buyer-try-on-act-${a.code}`}
                        active={on}
                        onClick={() => toggleActivity(a.code)}
                        variant="neutral"
                        size="md"
                      >
                        {a.label}
                      </PillChip>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              data-testid="buyer-try-on-generate"
              onClick={submit}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5">
              <Sparkles size={14} /> Try-On starten
            </button>
            <p className="text-[10px] text-[#999] text-center">{t("buyer_try_on.render_notice")}</p>
          </div>
        )}

        {(phase === "REQUESTING" || phase === "POLLING") && (
          <div className="p-8 text-center" data-testid="buyer-try-on-loading">
            <Loader2 className="animate-spin text-[#666] mx-auto mb-2" size={28} />
            <p className="text-sm">{t("buyer_try_on.ai_creating")}</p>
            <p className="text-[10px] text-[#999] mt-1">Kann bis zu 30 Sekunden dauern. Cache-Treffer sind sofort.</p>
          </div>
        )}

        {phase === "RESULT" && artifact && (
          <div className="p-3" data-testid="buyer-try-on-result">
            <div className="relative border border-[#E5E5E5]">
              <img src={artifact.url} alt="Vorschau" className="w-full max-h-[65vh] object-contain bg-black/5" data-testid="buyer-try-on-result-img" />
              <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-semibold px-2 py-1 rounded flex items-center gap-1">
                <Sparkles size={10} /> KI-Vorschau
              </div>
              {request?.cache_hit && (
                <div className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] font-semibold px-2 py-1 rounded" data-testid="buyer-try-on-cache-badge">Cache</div>
              )}
            </div>
            <div className="mt-2 flex items-start gap-2 text-[10px] text-[#666]">
              <CheckCircle size={12} className="text-emerald-600 shrink-0 mt-0.5" />
              <span>{t("buyer_try_on.simulation_note")}</span>
            </div>
            <button
              data-testid="buyer-try-on-retry"
              onClick={() => setPhase("CONFIG")}
              className="w-full h-10 mt-3 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm">
              Andere Einstellung probieren
            </button>
          </div>
        )}

        {phase === "ERROR" && (
          <div className="p-6 text-center" data-testid="buyer-try-on-error">
            <AlertCircle size={28} className="mx-auto text-red-500 mb-2" />
            <p className="text-sm text-red-700">{error || "Ein Fehler ist aufgetreten"}</p>
            <button onClick={() => setPhase("CONFIG")} className="mt-3 h-10 px-4 rounded-full border border-[#0A0A0A] text-sm">Erneut versuchen</button>
          </div>
        )}
      </div>

      {/* Vollbild-Zoom für Baseline-Posen */}
      {poseZoom !== null && (() => {
        const zp = cfg?.subject?.poses?.find((p) => p.pose_index === poseZoom);
        if (!zp?.baseline_url) return null;
        return (
          <div
            data-testid="buyer-try-on-zoom-overlay"
            className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center"
            onClick={() => setPoseZoom(null)}>
            <button
              data-testid="buyer-try-on-zoom-close"
              onClick={(e) => { e.stopPropagation(); setPoseZoom(null); }}
              className="absolute top-4 right-4 p-2 rounded-full bg-black/60 text-white z-10">
              <X size={22} />
            </button>
            <img src={zp.baseline_url} alt={`Pose ${zp.pose_index}`} className="max-w-full max-h-full object-contain" />
            <div className="absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none">
              <div className="pointer-events-auto flex gap-2">
                <button
                  data-testid="buyer-try-on-zoom-select"
                  onClick={(e) => { e.stopPropagation(); setPoseIndex(poseZoom); setPoseZoom(null); }}
                  className="h-11 px-5 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold">
                  Diese Pose w&auml;hlen
                </button>
              </div>
            </div>
            <div className="absolute top-4 left-4 bg-black/60 text-white text-[10px] font-semibold px-2 py-1 rounded flex items-center gap-1">
              <Sparkles size={10} /> KI-Vorschau · Wasserzeichen aktiv
            </div>
          </div>
        );
      })()}
    </div>
  );
}
